import { CdkDrag } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';

import { DirectionService, getTouchPosition, isTouchEvent } from '@hdms/shared/util-core';

import { ScrollCoords } from './types/scroll-coords';
import { ScrollDirection } from './types/scroll-direction';
import { Scrollable } from './types/scrollable';

export const SCROLLBAR_VISIBLE_CLASS = 'scrollbar-visible';

export interface ScrollStatus {
  visiblePercentage: ScrollCoords;
  thumbPosition: ScrollCoords;
}

@Component({
  selector: 'shrd-uic-scrollbar',
  templateUrl: './scrollbar.component.html',
  styleUrls: ['./scrollbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ScrollbarComponent implements AfterViewInit, OnChanges {
  static readonly updateTicks = 50;
  static readonly minThumbSize = 44;
  static readonly arrowDelta = 40; // Distance to scroll on arrow click in pixel

  @HostBinding('class.shrd-uic-scrollbar') readonly hostClass = true;

  @HostBinding('class.shrd-uic-scrollbar-horizontal')
  get horizontalClass(): boolean {
    return this.horizontal;
  }

  @HostBinding('class.shrd-uic-scrollbar-vertical')
  get verticalClass(): boolean {
    return !this.horizontal;
  }

  @HostBinding('style.transform') transform: string;

  @Input() horizontal = false;
  @Input() hasHorizontalArrows = false;
  @Input() external: boolean;
  @Input() transparentBackground = false;

  @Output() hidden = new EventEmitter<boolean>();

  @ViewChild(CdkDrag, { static: true }) thumb: CdkDrag;
  @ViewChild('track', { static: true }) track: ElementRef<HTMLDivElement>;

  position = { x: 0, y: 0 }; // Scrollbar position
  thumbSize = { width: null, height: null }; // Size of the thumb
  isRtl: boolean;

  @HostBinding('class.show-scrollbar-on-mobile')
  public visibleOnMobile = false;
  public isHidden: boolean;

  private trackSize = 0; // track dimensions
  private thumbPercentage: ScrollCoords = { horizontal: 0, vertical: 0 }; // Scrollbar position in percentage
  private scrollableReference: Scrollable; // reference to scrollbar directive
  private changeInterval: number;
  private isMoving = false;
  private viewInitialized: boolean;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly el: ElementRef,
    private readonly renderer: Renderer2,
    directionService: DirectionService
  ) {
    cdr.detach();
    this.isRtl = directionService.isRtl;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasHorizontalArrows && !changes.hasHorizontalArrows.firstChange) {
      this.cdr.reattach();
    }
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
  }

  onMoving(): void {
    this.isMoving = true;
    const { x: currentOffsetX, y: currentOffsetY } = this.thumb.getFreeDragPosition();

    let percentage: number;
    if (this.horizontal) {
      percentage = this.getThumbPositionPercentage(currentOffsetX, this.thumbSize.width);
    } else {
      percentage = this.getThumbPositionPercentage(currentOffsetY, this.thumbSize.height);
    }

    this.scrollableReference.setScrollPositionByExact(percentage, this.getDirection());
  }

  onMoveEnd(): void {
    this.isMoving = false;
  }

  onTrackDown(event: MouseEvent | TouchEvent): void {
    // don't proceed on right-click
    if (event instanceof MouseEvent && event.button === 2) {
      return;
    }

    let percentage: number;
    const pos = getTouchPosition(event);
    const trackRect = this.getTrackRect();
    if (this.horizontal) {
      percentage = (this.isRtl ? -1 : 0) + this.getTrackPercentage(pos.x, trackRect.left);
    } else {
      percentage = this.getTrackPercentage(pos.y, trackRect.top);
    }

    this.scrollableReference.setScrollPositionByExact(percentage, this.getDirection(), true);

    // "clone" the track mousedown/touchstart event and dispatch it on the thumb
    // for being able to immediately continue with dragging the thumb
    let thumbDownEvent: Event;
    if (isTouchEvent(event)) {
      const { touches, changedTouches, targetTouches } = event as TouchEvent;
      thumbDownEvent = new TouchEvent(event.type, {
        touches: Array.from(touches),
        changedTouches: Array.from(changedTouches),
        targetTouches: Array.from(targetTouches)
      });
    } else {
      thumbDownEvent = new MouseEvent(event.type, event);
    }
    this.thumb.element.nativeElement.dispatchEvent(thumbDownEvent);
  }

  onArrowDown(event: Event, first = false): void {
    event.preventDefault();
    this.changeInterval = setInterval(() => {
      let delta = ScrollbarComponent.arrowDelta;
      if (first) {
        delta *= -1;
      }
      this.scrollableReference.setScrollPositionByDelta(delta, this.getDirection());
    }, ScrollbarComponent.updateTicks) as any;
  }

  onArrowUp(event: Event): void {
    event.preventDefault();
    clearInterval(this.changeInterval);
  }

  public setThumbPosition(scrollPosition: number): void {
    if (!this.isMoving) {
      const prop = this.horizontal ? this.thumbSize.width : this.thumbSize.height;
      const pos = (this.trackSize - prop) * scrollPosition;
      this.position = {
        x: this.horizontal ? pos : 0,
        y: !this.horizontal ? pos : 0
      };

      this.cdr.detectChanges();
    }
  }

  public setVisibility(visiblePercentage: ScrollCoords = this.thumbPercentage): void {
    const dir = this.getDirection();
    const percentageVal = visiblePercentage[dir];
    if (percentageVal > 0.99) {
      this.hide();
      this.scrollableReference.hide(dir);
    } else {
      this.show();
      this.scrollableReference.show(dir);
    }
  }

  public bindReference(scrollableRef: Scrollable): void {
    this.scrollableReference = scrollableRef;
  }

  public unbindReference(): void {
    this.scrollableReference = null;
  }

  public update({ visiblePercentage, thumbPosition }: ScrollStatus): void {
    if (!this.viewInitialized) {
      // ensure correct initial calculation of track size/position
      this.cdr.detectChanges();
    }

    this.setVisibility(visiblePercentage);
    this.setTrackSize();
    this.setThumbSize(visiblePercentage);
    this.setThumbPosition(thumbPosition[this.getDirection()]);
  }

  public setTrackCompensation(scrollDistance: ScrollCoords): void {
    if (!this.external) {
      this.transform = `translate3d(${scrollDistance.horizontal}px,${scrollDistance.vertical}px,0)`;
    }
  }

  public hide(emit = true): void {
    if (this.isHidden !== true) {
      this.isHidden = true;
      this.renderer.removeClass(this.el.nativeElement, SCROLLBAR_VISIBLE_CLASS);
      if (emit) {
        this.hidden.emit(true);
      }
    }
  }

  private show(): void {
    if (this.isHidden !== false) {
      this.isHidden = false;
      this.renderer.addClass(this.el.nativeElement, SCROLLBAR_VISIBLE_CLASS);
      this.hidden.emit(false);
    }
  }

  private setTrackSize(): void {
    const trackRect = this.getTrackRect();
    this.trackSize = this.horizontal ? trackRect.width : trackRect.height;
  }

  private setThumbSize(thumbPercentage: ScrollCoords = this.thumbPercentage): void {
    this.thumbPercentage = thumbPercentage;
    const percentageVal = thumbPercentage[this.getDirection()];
    const dimension = Math.max(this.trackSize * percentageVal, ScrollbarComponent.minThumbSize);

    if (this.horizontal) {
      this.thumbSize.width = dimension;
    } else {
      this.thumbSize.height = dimension;
    }

    this.cdr.detectChanges();
  }

  private getThumbPositionPercentage(coordinate: number, thumb: number): number {
    return coordinate / (this.trackSize - thumb);
  }

  private getTrackPercentage(coordinate: number, offSet: number): number {
    return (coordinate - offSet) / this.trackSize;
  }

  private getTrackRect(): DOMRect {
    return this.track.nativeElement.getBoundingClientRect();
  }

  private getDirection(): ScrollDirection {
    return this.horizontal ? ScrollDirection.HORIZONTAL : ScrollDirection.VERTICAL;
  }
}
